// src/contexts/SubscriptionContext.js
// Centralised subscription state. Fetches /api/billing/subscription once
// the user is authenticated and exposes a refresh() so flows that change
// subscription state (purchase, cancel) can force a re-fetch.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AuthContext } from './AuthContext';
import { getMySubscription } from '../utils/billingApi';

export const SubscriptionContext = createContext({
  loading: false,
  subscription: null,
  derived: { hasAccess: true, daysRemaining: 0, accessType: 'unknown' },
  employeeCount: 0,
  recentInvoices: [],
  refresh: async () => {},
  error: null,
});

export const SubscriptionProvider = ({ children }) => {
  const { auth } = useContext(AuthContext);
  const [state, setState] = useState({
    loading: false,
    subscription: null,
    derived: { hasAccess: true, daysRemaining: 0, accessType: 'unknown' },
    employeeCount: 0,
    recentInvoices: [],
    error: null,
    fetchedAt: null,
  });
  const inFlightRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!auth) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await getMySubscription();
      setState({
        loading: false,
        subscription: data.subscription || null,
        derived: data.derived || { hasAccess: true, daysRemaining: 0, accessType: 'unknown' },
        employeeCount: data.employeeCount || 0,
        recentInvoices: data.recentInvoices || [],
        error: null,
        fetchedAt: Date.now(),
      });
    } catch (err) {
      // Fail-open: never block the app on a billing endpoint failure.
      console.warn('[SubscriptionContext] refresh failed:', err);
      setState((s) => ({
        ...s,
        loading: false,
        error: err.message || 'Failed to load subscription',
      }));
    } finally {
      inFlightRef.current = false;
    }
  }, [auth]);

  useEffect(() => {
    if (auth) refresh();
    else setState((s) => ({ ...s, subscription: null, employeeCount: 0, recentInvoices: [] }));
  }, [auth, refresh]);

  const value = useMemo(() => ({ ...state, refresh }), [state, refresh]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
